'use client';

import { Suspense, useEffect } from 'react';
import { useAdContext } from '@/app_lib/providers/AdProvider';
import { useUser } from '@/app_lib/providers/UserProvider';
import { GoogleTagManager } from '@/app_lib/services/Gtm';
import type { PageAdConfig } from '@/app_lib/types/Ad';
import type { ArcSpanPageConfig } from '@/app_lib/types/ArcSpan';
import type { PianoPageConfig } from '@/app_lib/types/accounts/piano/Piano';
import type { PageViewDataLayerObject } from '@/app_lib/types/Gtm';

/**
 * PageWrapper component sets up client-sided page-level configurations.
 *
 * @param children - React children nodes to be rendered inside the wrapper.
 * @param pageConfig - Configuration object for Piano page settings.
 * @param arcSpanConfig - Configuration object for ArcSpan page settings.
 * @param pageAdConfig - Configuration object for page advertisement settings.
 *
 * @remarks
 * - Initializes Piano, ArcSpan, and Ad context configurations on mount.
 */
export const PageWrapper = ({
  className,
  children,
  pageConfig,
  arcSpanConfig,
  pageAdConfig,
  dataLayer,
}: {
  className?: string;
  children?: React.ReactNode;
  pageConfig: PianoPageConfig;
  arcSpanConfig: ArcSpanPageConfig;
  pageAdConfig: PageAdConfig;
  dataLayer: PageViewDataLayerObject;
}) => {
  const { setPianoPageConfig } = useUser();
  const { setPageAdConfig, setArcSpanPageConfig } = useAdContext();

  useEffect(() => {
    setPianoPageConfig(pageConfig);
    setArcSpanPageConfig(arcSpanConfig);
    setPageAdConfig(pageAdConfig);
  }, [setPianoPageConfig, pageConfig, arcSpanConfig, pageAdConfig, setPageAdConfig, setArcSpanPageConfig]);

  return (
    <div className={className}>
      <Suspense fallback={null}>
        <GoogleTagManager dataLayer={dataLayer} />
      </Suspense>
      {children}
    </div>
  );
};
